<template>

  <div 
    v-if="!isLoggedIn"
    class="col-span-12 page-grid m-0 p-0"
  >
    <p class="col-span-12 text-center">
      You must be logged in to view this page.
    </p>
    <p class="col-span-12 text-center">
      Redirecting to the login page.
    </p>
  </div>

  <div 
    v-else-if="order"
    class="col-span-12 page-grid m-0 p-0"
  >
    <div class="col-span-12 page-grid m-0 p-0">
      <div class="col-span-12 lg:col-span-6 lg:order-1 order-2">
        <p><strong class="font-semibold">Relias LLC</strong></p> 
        <p>Attn: WCEI</p>
        <p>1010 Sync Street</p>
        <p>Morrisville, NC 27560</p>
        <p>1-877-462-9234</p>
      </div>
      <div class="col-span-12 lg:col-span-6 lg:order-2 order-1 flex justify-end">
        <svg
          width="300"
          height="100"
          viewBox="0 0 207 40"
          aria-hidden="true"
        >
          <use xlink:href="#sprite-logo-filled" />
        </svg>
      </div>
    </div>

    <div class="col-span-12 m-0 p-0 border-b border-gray-300">
      <p class="heading-xs inline-block relative top-[2px] bg-white pr-[8px]">
        Registration # {{ order.registration_number }} approved on {{ formatDate(order.created_at) }}
      </p>
    </div>
    
    <div class="col-span-12 m-0 p-0">
      <div>
        <p class="w-max"><strong class="font-semibold">{{ order.first_name }} {{ order.last_name }}</strong></p> 
        <p class="w-max">{{ order.course_type }}</p>
      </div>
    </div>

    <div class="col-span-12 page-grid m-0 p-0">
      <div class="col-span-12 rounded-lg border border-gray-300 overflow-x-auto">
        <table class="w-full table-fixed text-left">
          <thead class="bg-gray-100 text-gray-800 font-semibold border-b border-gray-300">
            <tr>
              <th class="px-4 py-3 w-[35%] font-normal">Paid By</th>
              <th class="px-4 py-3 w-[30%] font-normal">Payment Information</th>
              <th class="px-4 py-3 w-[20%] font-normal">Paid Date</th>
              <th class="px-4 py-3 w-[15%] font-normal">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="px-4 py-3">
                <p>{{ order.first_name }} {{ order.last_name }}</p>
                <p>{{ order.street }}</p>
                <p>{{ order.city }}, {{ order.state }} {{ order.zip }}</p>
                <p>{{ order.email }}</p>
              </td>
              <td class="px-4 py-3">
                <p>
                  {{ order.payment_method }} 
                  <span v-if="order.last4cc">xxxxxxxxxxx{{ order.last4cc }}</span>
                </p>
              </td>
              <td class="px-4 py-3">{{ formatDate(order.created_at) }}</td>
              <td class="px-4 py-3">${{ order.grand_total}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div 
    v-else-if="orderError"
    class="col-span-12 page-grid m-0 p-0 text-center"
  >
    <p class="col-span-12">There was a problem loading the receipt.</p>
    <p class="col-span-12">If the problem persists please contact us at <a href="mailto:woundcaresupport@relias.com" class="text-primary-500 underline">woundcaresupport@relias.com</a></p>
    <p class="col-span-12">Redirecting to your <a href="/account/dashboard" class="text-primary-500 underline">dashboard</a> in {{ countdown }} seconds...</p>
  </div>

  <div 
    v-else
    class="col-span-12 page-grid m-0 p-0 text-center"
  >
    <p class="col-span-12">Loading receipt...</p>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { getCustomerOrder, type CustomerOrder } from '@js/api/customer/getCustomerOrder'
import { format } from 'date-fns'
import { log } from '@js/utils/logger'

import useAuth from '@js/composables/useAuth'

defineOptions({
  name: 'AppReceipt',
})

const props = defineProps<{
  orderId?: string
}>()

const { isLoggedIn } = useAuth()

const order = ref<CustomerOrder | null>(null)
const orderError = ref<boolean>(false)
const countdown = ref<number>(30)

const formatDate = (date: string) => {
  return format(new Date(date), 'MM/dd/yyyy')
}

watch(orderError, (isError) => {
  if (isError) {
    const interval = setInterval(() => {
      countdown.value--

      if (countdown.value <= 0) {
        clearInterval(interval)
        window.location.href = '/customer/dashboard'
      }
    }, 1000)
  }
})

onMounted(async () => {
  if (!isLoggedIn.value) {
    window.location.href = '/account/login'
    return
  }

  if (!props.orderId) return

  try {
    order.value = await getCustomerOrder(parseInt(props.orderId))
  } catch (error) {
    orderError.value = true
    log({
      message: `Error fetching order: ${error}`,
      file: 'src/wcei/js/components/account/dashboard/AppReceipt.vue',
    })
  }
})
</script>
